<template>
  <div class="bookshop">

    <!-- Header: year + topic filter -->
    <div class="bookshop-header">
      <span class="bookshop-year" v-if="props.block.content.year">{{ props.block.content.year }}</span>
      <select class="pill-select topic-filter" v-model="selectedTopic" v-if="allTopics.length">
        <option value="">Browse by Topic: All</option>
        <option v-for="t in allTopics" :key="t" :value="t">{{ t }}</option>
      </select>
    </div>

    <div class="bookshop-item" v-for="book in filteredBooks" :key="book.uid">
      <hr class="book-rule" />
      <div class="book-compact">

        <!-- Cover -->
        <div class="book-cover">
          <img v-if="book.coverimages?.[0]?.url" :src="book.coverimages[0].url" :alt="book.title" />
          <div v-else class="cover-empty" />
        </div>

        <!-- Details -->
        <div class="book-details">
          <p class="book-title">{{ book.title }}</p>
          <p class="book-price" v-if="priceOf(book)">${{ priceOf(book) }}</p>
          <div class="book-specs" v-if="book.specs">{{ book.specs }}</div>

          <div class="book-actions">
            <!-- Left: Subtotal + Add to Cart -->
            <div class="action-col">
              <span class="subtotal">Subtotal&nbsp;&nbsp;${{ subtotalOf(book).toFixed(2) }}</span>
              <button class="add-btn" @click="addToCart(book)">Add to Cart</button>
            </div>

            <!-- Right: Quantity + Order type -->
            <div class="action-col">
              <label class="control-row">
                <span class="control-label">Quantity</span>
                <select class="pill-select" :value="getQty(book)" @change="quantities[book.uid] = +$event.target.value">
                  <option v-for="n in 10" :key="n" :value="n">{{ n }}</option>
                </select>
              </label>
              <label class="control-row">
                <span class="control-label">Order type:</span>
                <select class="pill-select" v-model="cart.orderType">
                  <option value="individual">Individual</option>
                  <option value="institutional">Institutional</option>
                </select>
              </label>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, reactive, ref } from 'vue'
  import { useStore } from '../KQL.js'
  import { pageFromPath } from '../helpers.js'
  import { useCartStore } from './CartStore.js'

  const kql = useStore();
  const cart = useCartStore();
  const props = defineProps(['block', 'page']);

  const selectedTopic = ref('');

  const books = computed(() => {
    return (props.block.content.rows || [])
      .map(row => row.page?.[0])
      .filter(Boolean)
      .map(uid => pageFromPath(uid, kql.pages))
      .filter(Boolean);
  });

  const topicsOf = (book) => {
    if (!book.topics) return [];
    if (Array.isArray(book.topics)) return book.topics;
    return book.topics.split(',').map(t => t.trim()).filter(Boolean);
  };

  const allTopics = computed(() => {
    const set = new Set();
    books.value.forEach(b => topicsOf(b).forEach(t => set.add(t)));
    return [...set].sort();
  });

  const filteredBooks = computed(() => {
    if (!selectedTopic.value) return books.value;
    return books.value.filter(b => topicsOf(b).includes(selectedTopic.value));
  });

  // Per-book quantity, default 1
  const quantities = reactive({});
  const getQty = (book) => quantities[book.uid] ?? 1;

  const priceOf = (book) => {
    const p = book?.price;
    if (!p || parseFloat(p) === 0) return null;
    return parseFloat(p).toFixed(2);
  };

  const subtotalOf = (book) => {
    const p = parseFloat(book?.price) || 0;
    return p * getQty(book);
  };

  const addToCart = async (book) => {
    const qty = getQty(book);
    const existing = cart.line_items.find(i => i._slug === book.uid);
    if (existing) {
      cart.updateQuantity(book.uid, existing.quantity + qty);
      cart.isOpen = true;
      cart.loadSession();
    } else {
      await cart.addItem(book);
      if (qty > 1) cart.updateQuantity(book.uid, qty);
    }
  };
</script>

<style scoped>
@import '../css/breakpoints.css';

  .bookshop {
    margin-top: 4rem;
  }

  .bookshop-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
  }

  .bookshop-year {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 2.4rem;
    color: #f1f2f2;
  }

  .book-rule {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.3);
    margin: 0 0 3.5rem;
  }

  .bookshop-item {
    margin-bottom: 4rem;
  }

  .book-compact {
    display: flex;
    gap: 4rem;
    align-items: center;
  }

  /* Cover */
  .book-cover {
    flex-shrink: 0;
    width: 20rem;
  }

  .book-cover img {
    width: 100%;
    display: block;
    object-fit: contain;
  }

  .cover-empty {
    width: 100%;
    aspect-ratio: 346 / 556;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 2px;
  }

  /* Details */
  .book-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    min-width: 0;
  }

  .book-title {
    font-family: "Copernicus", serif;
    font-size: 3rem;
    font-weight: normal;
    line-height: 1.2;
    color: #f1f2f2;
    max-width: 60rem;
    margin: 0;
  }

  .book-year {
    opacity: 0.7;
  }

  .book-price {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.8rem;
    color: #f1f2f2;
    margin: 0;
  }

  .book-specs {
    font-family: "Copernicus", serif;
    font-size: 1.6rem;
    line-height: 1.4;
    color: #f1f2f2;
    max-width: 80rem;
    opacity: 0.85;
  }

  /* Actions */
  .book-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 1rem 3rem;
  }

  .action-col {
    display: flex;
    align-items: baseline;
    gap: 1.5rem;
  }

  .subtotal {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: #f1f2f2;
    white-space: nowrap;
  }

  .add-btn {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: #f1f2f2;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 3em;
    padding: 0.4em 1.6em 0.45em;
    cursor: pointer;
    transition: background 0.18s;
    white-space: nowrap;
  }

  .add-btn:hover {
    background: rgba(255, 255, 255, 0.28);
  }

  .control-row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    cursor: default;
  }

  .control-label {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: #f1f2f2;
    white-space: nowrap;
  }

  .pill-select {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: var(--ui);
    color: #f1f2f2;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 3em;
    padding: 0.2em 2em 0.2em 0.9em;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0l3.5 4 3.5-4z' fill='white'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.7em center;
  }

  .pill-select:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }

  .pill-select option {
    color: #000;
    background: #fff;
  }

  .topic-filter {
    padding-right: 2.5em;
    padding-left: 0.9em;
    background-position: right 0.8em center;
  }

  /* Mobile */
  @media (--phone) {
    .book-compact {
      flex-direction: column;
      gap: 2.5rem;
    }

    .book-cover {
      width: 100%;
      max-width: 22rem;
    }

    .book-title {
      font-size: 2.4rem;
    }

    .book-actions {
      flex-direction: column;
      align-items: flex-start;
      gap: 2rem;
    }
  }
</style>
